import React, { useState, useEffect } from 'react';
import { Search, Send, X, Plus, Trash2, CircleAlert as AlertCircle, CircleCheck as CheckCircle, Clock, ChevronDown, ChevronUp, History } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import InputMask from 'react-input-mask-next';

interface Installment {
  id: string;
  due_date: string;
  amount: number;
  status: string;
  paid_amount: number;
  interest: number;
}

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
  loan_amount: number;
  installments: number;
  total_amount: number;
  interest_rate: number;
  ruta: number;
  observations: string;
  service_entry_time: string;
  service_exit_time: string;
  approval_status: string;
  installments_data: Installment[];
}

interface EditRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  changes: FieldChange[];
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  contract: { client_name: string } | null;
}

interface FieldChange {
  field: string;
  label: string;
  old_value: string;
  new_value: string;
}

interface RelativePhone {
  numero: string;
  relacao: string;
}

const CONTRACT_FIELDS: { key: keyof Omit<Contract, 'installments_data' | 'relative_phones' | 'approval_status'>; label: string; type?: string }[] = [
  { key: 'client_name', label: 'Nome' },
  { key: 'phone', label: 'Telefone' },
  { key: 'email', label: 'E-mail', type: 'email' },
  { key: 'address', label: 'Endereco Residencial' },
  { key: 'business_address', label: 'Endereco Comercial' },
  { key: 'pix_key', label: 'Chave PIX' },
  { key: 'bank', label: 'Banco' },
  { key: 'loan_amount', label: 'Valor do Emprestimo', type: 'number' },
  { key: 'total_amount', label: 'Total a Pagar', type: 'number' },
  { key: 'installments', label: 'Numero de Parcelas', type: 'number' },
  { key: 'interest_rate', label: 'Taxa de Juros (%)', type: 'number' },
  { key: 'ruta', label: 'Ruta', type: 'number' },
  { key: 'service_entry_time', label: 'Horario de Entrada', type: 'time' },
  { key: 'service_exit_time', label: 'Horario de Saida', type: 'time' },
  { key: 'observations', label: 'Observacoes' },
];

function formatCurrency(v: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

export default function SellerClientEdit() {
  const { user } = useAuth();
  const [searchType, setSearchType] = useState<'name' | 'cpf'>('name');
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Contract[]>([]);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [editedData, setEditedData] = useState<Partial<Contract>>({});
  const [editedRelativePhones, setEditedRelativePhones] = useState<RelativePhone[]>([]);
  const [editedInstallments, setEditedInstallments] = useState<Installment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [myRequests, setMyRequests] = useState<EditRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (user) fetchMyRequests();
  }, [user]);

  const fetchMyRequests = async () => {
    if (!user) return;
    setLoadingRequests(true);
    try {
      const { data } = await supabase
        .from('client_edit_requests')
        .select('id, status, changes, rejection_reason, created_at, reviewed_at, contract:contracts(client_name)')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      setMyRequests((data as any) || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    setSearching(true);
    setMessage(null);
    setSearchResults([]);
    try {
      let query = supabase
        .from('contracts')
        .select('id, client_name, cpf, phone, relative_phones, email, address, business_address, pix_key, bank, loan_amount, installments, total_amount, interest_rate, ruta, observations, service_entry_time, service_exit_time, approval_status, installments_data:installments(*)')
        .eq('unit_id', user?.unit_id);

      if (searchType === 'name') {
        query = query.ilike('client_name', `%${searchValue}%`);
      } else {
        query = query.eq('cpf', searchValue.replace(/\D/g, ''));
      }

      const { data, error } = await query.order('client_name').limit(10);
      if (error) throw error;
      if (!data || data.length === 0) {
        setMessage({ type: 'error', text: 'Nenhum cliente encontrado' });
      } else {
        setSearchResults(data as any);
      }
    } catch {
      setMessage({ type: 'error', text: 'Erro ao buscar cliente' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectContract = (contract: Contract) => {
    setSelectedContract(contract);
    setEditedData({ ...contract });
    setEditedRelativePhones(contract.relative_phones || []);
    const sorted = [...(contract.installments_data || [])].sort((a, b) => a.due_date.localeCompare(b.due_date));
    setEditedInstallments(sorted);
    setSearchResults([]);
    setSearchValue('');
    setMessage(null);
  };

  const handleInstallmentChange = (index: number, field: keyof Installment, value: any) => {
    const updated = [...editedInstallments];
    (updated[index] as any)[field] = value;
    setEditedInstallments(updated);
  };

  const handleSubmit = async () => {
    if (!selectedContract || !user) return;

    const changes: FieldChange[] = [];

    // Contract field changes
    CONTRACT_FIELDS.forEach(({ key, label }) => {
      const oldVal = String(selectedContract[key as keyof Contract] ?? '');
      const newVal = String(editedData[key as keyof Contract] ?? '');
      if (oldVal !== newVal) {
        changes.push({ field: key, label, old_value: oldVal, new_value: newVal });
      }
    });

    // Relative phones
    const oldPhones = JSON.stringify(selectedContract.relative_phones || []);
    const newPhones = JSON.stringify(editedRelativePhones);
    if (oldPhones !== newPhones) {
      changes.push({
        field: 'relative_phones',
        label: 'Telefones de Parentes',
        old_value: (selectedContract.relative_phones || []).map(p => `${p.relacao}: ${p.numero}`).join(', ') || '—',
        new_value: editedRelativePhones.map(p => `${p.relacao}: ${p.numero}`).join(', ') || '—',
      });
    }

    // Installment changes — stored as JSON in new_value for the approve step
    const origSorted = [...(selectedContract.installments_data || [])].sort((a, b) => a.due_date.localeCompare(b.due_date));
    const instChanges: { index: number; id: string; fields: { field: string; old: string; new: string }[] }[] = [];
    editedInstallments.forEach((inst, i) => {
      const orig = origSorted[i];
      if (!orig || orig.id !== inst.id) return;
      const fieldDiffs: { field: string; old: string; new: string }[] = [];
      (['due_date', 'amount', 'status', 'paid_amount', 'interest'] as (keyof Installment)[]).forEach(f => {
        const o = String(orig[f] ?? '');
        const n = String(inst[f] ?? '');
        if (o !== n) fieldDiffs.push({ field: f, old: o, new: n });
      });
      if (fieldDiffs.length > 0) instChanges.push({ index: i + 1, id: inst.id, fields: fieldDiffs });
    });

    if (instChanges.length > 0) {
      const oldSummary = instChanges.map(c => `Parcela ${c.index}: ${c.fields.map(f => `${f.field}=${f.old}`).join(', ')}`).join(' | ');
      const newSummary = instChanges.map(c => `Parcela ${c.index}: ${c.fields.map(f => `${f.field}=${f.new}`).join(', ')}`).join(' | ');
      changes.push({
        field: 'installments_data',
        label: `Parcelas (${instChanges.length} alterada${instChanges.length > 1 ? 's' : ''})`,
        old_value: oldSummary,
        new_value: JSON.stringify(instChanges),
      });
    }

    if (changes.length === 0) {
      setMessage({ type: 'error', text: 'Nenhuma alteracao detectada' });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const { data: request, error: reqError } = await supabase
        .from('client_edit_requests')
        .insert({
          contract_id: selectedContract.id,
          seller_id: user.id,
          unit_id: user.unit_id,
          changes,
        })
        .select('id')
        .single();

      if (reqError) throw reqError;

      const { data: managers } = await supabase
        .from('users')
        .select('id')
        .eq('unit_id', user.unit_id)
        .eq('role', 'manager');

      if (managers && managers.length > 0) {
        const notifs = managers.map(m => ({
          user_id: m.id,
          type: 'client_edit_request',
          title: 'Solicitacao de Edicao de Cliente',
          message: `O vendedor ${user.name} solicitou alteracoes no cadastro de ${selectedContract.client_name}.`,
          is_read: false,
          metadata: {
            request_id: request.id,
            client_name: selectedContract.client_name,
            seller_name: user.name,
            changes_count: changes.length,
          },
        }));
        await supabase.from('notifications').insert(notifs);
      }

      setMessage({ type: 'success', text: 'Solicitacao enviada! Aguardando aprovacao do gestor.' });
      setSelectedContract(null);
      setEditedData({});
      setEditedRelativePhones([]);
      setEditedInstallments([]);
      fetchMyRequests();
    } catch {
      setMessage({ type: 'error', text: 'Erro ao enviar solicitacao' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    if (status === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"><Clock className="w-3 h-3" />Pendente</span>;
    if (status === 'approved') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Aprovado</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><X className="w-3 h-3" />Recusado</span>;
  };

  if (user?.role !== 'seller') {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-600">Acesso restrito a vendedores.</p>
        </div>
      </div>
    );
  }

  const pendingCount = myRequests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Solicitar Edicao de Cliente</h1>
        <p className="text-sm text-gray-500 mt-1">As alteracoes serao revisadas pelo gestor antes de serem aplicadas.</p>
      </div>

      {message && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {/* Search */}
      {!selectedContract && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Buscar Cliente</h2>
          <div className="flex gap-4 mb-4">
            {(['name', 'cpf'] as const).map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value={t} checked={searchType === t} onChange={() => setSearchType(t)} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-700">{t === 'name' ? 'Nome' : 'CPF'}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            {searchType === 'cpf' ? (
              <InputMask
                mask="999.999.999-99"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder="000.000.000-00"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            ) : (
              <input
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Nome do cliente..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            )}
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              <Search className="w-4 h-4" />
              {searching ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              {searchResults.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleSelectContract(c)}
                  className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <p className="font-semibold text-gray-900 group-hover:text-blue-700">{c.client_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">CPF: {c.cpf} · Ruta {c.ruta}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit form */}
      {selectedContract && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">{selectedContract.client_name}</h2>
              <p className="text-xs text-gray-500">CPF: {selectedContract.cpf} · Ruta {selectedContract.ruta}</p>
            </div>
            <button
              onClick={() => { setSelectedContract(null); setEditedData({}); setMessage(null); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Dados Pessoais */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Dados Pessoais</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome</label>
                <input type="text" value={editedData.client_name || ''} onChange={e => setEditedData(d => ({ ...d, client_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefone</label>
                <InputMask mask="(99) 99999-9999" value={editedData.phone || ''} onChange={e => setEditedData(d => ({ ...d, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-mail</label>
                <input type="email" value={editedData.email || ''} onChange={e => setEditedData(d => ({ ...d, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Chave PIX</label>
                <input type="text" value={editedData.pix_key || ''} onChange={e => setEditedData(d => ({ ...d, pix_key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Banco</label>
                <input type="text" value={editedData.bank || ''} onChange={e => setEditedData(d => ({ ...d, bank: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>

            {/* Relative phones */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-600">Telefones de Parentes</label>
                <button onClick={() => setEditedRelativePhones(p => [...p, { numero: '', relacao: '' }])}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                  <Plus className="w-3.5 h-3.5" /> Adicionar
                </button>
              </div>
              <div className="space-y-2">
                {editedRelativePhones.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <InputMask mask="(99) 99999-9999" value={p.numero}
                      onChange={e => { const u = [...editedRelativePhones]; u[i].numero = e.target.value; setEditedRelativePhones(u); }}
                      placeholder="Telefone"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    <input type="text" value={p.relacao}
                      onChange={e => { const u = [...editedRelativePhones]; u[i].relacao = e.target.value; setEditedRelativePhones(u); }}
                      placeholder="Relacao (ex: Mae)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    <button onClick={() => setEditedRelativePhones(editedRelativePhones.filter((_, idx) => idx !== i))}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {editedRelativePhones.length === 0 && <p className="text-xs text-gray-400 py-1">Nenhum telefone de parente cadastrado</p>}
              </div>
            </div>
          </div>

          {/* Enderecos */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Enderecos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Endereco Residencial</label>
                <textarea rows={2} value={editedData.address || ''} onChange={e => setEditedData(d => ({ ...d, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Endereco Comercial</label>
                <textarea rows={2} value={editedData.business_address || ''} onChange={e => setEditedData(d => ({ ...d, business_address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
            </div>
          </div>

          {/* Dados do Contrato */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Dados do Contrato</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Valor do Emprestimo</label>
                <input type="number" step="0.01" value={editedData.loan_amount ?? ''} onChange={e => setEditedData(d => ({ ...d, loan_amount: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Total a Pagar</label>
                <input type="number" step="0.01" value={editedData.total_amount ?? ''} onChange={e => setEditedData(d => ({ ...d, total_amount: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Num. Parcelas</label>
                <input type="number" value={editedData.installments ?? ''} onChange={e => setEditedData(d => ({ ...d, installments: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Taxa de Juros (%)</label>
                <input type="number" step="0.01" value={editedData.interest_rate ?? ''} onChange={e => setEditedData(d => ({ ...d, interest_rate: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
          </div>

          {/* Dados Operacionais */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Dados Operacionais</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ruta</label>
                <input type="number" value={editedData.ruta ?? ''} onChange={e => setEditedData(d => ({ ...d, ruta: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Horario de Entrada</label>
                <input type="time" value={editedData.service_entry_time || ''} onChange={e => setEditedData(d => ({ ...d, service_entry_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Horario de Saida</label>
                <input type="time" value={editedData.service_exit_time || ''} onChange={e => setEditedData(d => ({ ...d, service_exit_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Observacoes</label>
              <textarea rows={3} value={editedData.observations || ''} onChange={e => setEditedData(d => ({ ...d, observations: e.target.value }))}
                placeholder="Observacoes sobre o contrato..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
            </div>
          </div>

          {/* Parcelas */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Parcelas</h3>
            {editedInstallments.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhuma parcela encontrada.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">#</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Vencimento</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Valor</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Valor Pago</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Juros</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {editedInstallments.map((inst, i) => (
                      <tr key={inst.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500 font-medium">{i + 1}</td>
                        <td className="px-3 py-2">
                          <input type="date" value={inst.due_date || ''} onChange={e => handleInstallmentChange(i, 'due_date', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none w-36" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={inst.amount ?? ''} onChange={e => handleInstallmentChange(i, 'amount', parseFloat(e.target.value))}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none w-24" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={inst.status} onChange={e => handleInstallmentChange(i, 'status', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none">
                            <option value="pending">Pendente</option>
                            <option value="paid">Paga</option>
                            <option value="overdue">Atrasada</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={inst.paid_amount ?? ''} onChange={e => handleInstallmentChange(i, 'paid_amount', parseFloat(e.target.value))}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none w-24" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={inst.interest ?? ''} onChange={e => handleInstallmentChange(i, 'interest', parseFloat(e.target.value))}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none w-20" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Submit footer */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2 text-amber-700">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs">As alteracoes serao enviadas para aprovacao do gestor. Nenhuma mudanca sera aplicada ate a aprovacao.</p>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Enviando...' : 'Enviar para Aprovacao'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <button onClick={() => setShowHistory(v => !v)} className="w-full flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-gray-800 text-sm">Minhas Solicitacoes</span>
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">{pendingCount} pendente{pendingCount > 1 ? 's' : ''}</span>
            )}
          </div>
          {showHistory ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {showHistory && (
          <div className="border-t border-gray-100">
            {loadingRequests ? (
              <div className="p-6 text-center text-sm text-gray-500">Carregando...</div>
            ) : myRequests.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">Nenhuma solicitacao enviada ainda.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {myRequests.map(req => (
                  <div key={req.id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{(req.contract as any)?.client_name || '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(req.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                      {statusBadge(req.status)}
                    </div>
                    <div className="space-y-1">
                      {req.changes.map((c, i) => (
                        <div key={i} className="text-xs text-gray-600">
                          <span className="font-medium">{c.label}:</span>{' '}
                          {c.field === 'installments_data' ? (
                            <span className="text-gray-500 italic">{req.changes.find(ch => ch.field === 'installments_data')?.label}</span>
                          ) : (
                            <>
                              <span className="line-through text-gray-400">{c.old_value || '—'}</span>{' '}
                              <span className="text-gray-700">→ {c.new_value || '—'}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {req.status === 'rejected' && req.rejection_reason && (
                      <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-medium text-red-800">Motivo da recusa:</p>
                        <p className="text-xs text-red-700 mt-0.5">{req.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}